import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../../api/apiClient";
import flowerIcon from "../../../assets/img/flower_icon.png";
import "../../../assets/css/admin/organization/OrganizationAdmin.css";
import "../../../assets/css/admin/AdminCommon.css";

const ITEMS_PER_PAGE = 10;

const normalizeUnit = (item) => ({
  id: item.orgUnitId,
  name: item.unitName || "",
  director: item.headName || "",
  info: item.headStudentId || "",
  members:
    item.members?.map((member) => member.memberName || "")?.filter(Boolean) ||
    [],
});

export default function OrgOffice() {
  const [modalOpen, setModalOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [form, setForm] = useState({
    id: null,
    name: "",
    director: "",
    info: "",
    members: [""],
  });

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);

      const result = await apiClient("/api/council/org/units");
      const data = result?.data || [];

      if (!Array.isArray(data)) {
        setDepartments([]);
        return;
      }

      setDepartments(data.map(normalizeUnit));
    } catch (error) {
      console.error("중앙집행국 조회 오류:", error);
      alert(error.message || "중앙집행국 조회 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const totalPages = Math.ceil(departments.length / ITEMS_PER_PAGE);

  const pagedDepartments = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return departments.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [departments, currentPage]);

  useEffect(() => {
    if (currentPage > (totalPages || 1)) {
      setCurrentPage(totalPages || 1);
    }
  }, [currentPage, totalPages]);

  const openAddModal = () => {
    setEditIndex(null);

    setForm({
      id: null,
      name: "",
      director: "",
      info: "",
      members: [""],
    });

    setModalOpen(true);
  };

  const openEditModal = (index) => {
    setEditIndex(index);

    const selectedDepartment = departments[index];

    setForm({
      id: selectedDepartment.id,
      name: selectedDepartment.name || "",
      director: selectedDepartment.director || "",
      info: selectedDepartment.info || "",
      members: selectedDepartment.members?.length
        ? [...selectedDepartment.members]
        : [""],
    });

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditIndex(null);
  };

  const saveDepartment = async () => {
    if (!form.name.trim()) {
      alert("집행국 이름을 입력해 주세요.");
      return;
    }

    if (!form.director.trim()) {
      alert("국장 이름을 입력해 주세요.");
      return;
    }

    if (!form.info.trim()) {
      alert("국장 전공/학번을 입력해 주세요.");
      return;
    }

    const requestBody = {
      unitName: form.name,
      headName: form.director,
      headStudentId: form.info,
      members: form.members.filter((member) => member.trim() !== ""),
    };

    try {
      setLoading(true);

      if (editIndex === null) {
        const result = await apiClient("/api/council/org/units", {
          method: "POST",
          body: JSON.stringify(requestBody),
        });

        const newDepartment = normalizeUnit(result.data);

        setDepartments((prev) => {
          const next = [...prev, newDepartment];
          setCurrentPage(Math.ceil(next.length / ITEMS_PER_PAGE));
          return next;
        });

        alert("추가되었습니다.");
      } else {
        const targetId = form.id;

        const result = await apiClient(`/api/council/org/units/${targetId}`, {
          method: "PUT",
          body: JSON.stringify(requestBody),
        });

        const updatedDepartment = normalizeUnit(result.data);

        setDepartments((prev) =>
          prev.map((item, index) =>
            index === editIndex ? updatedDepartment : item
          )
        );

        alert("수정되었습니다.");
      }

      closeModal();
    } catch (error) {
      console.error("중앙집행국 저장 오류:", error);
      alert(error.message || "중앙집행국 저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const deleteDepartment = async (index) => {
    const confirmDelete = window.confirm("삭제하시겠습니까?");
    if (!confirmDelete) return;

    const target = departments[index];

    if (!target?.id) {
      alert("삭제할 집행국 정보를 찾을 수 없습니다.");
      return;
    }

    try {
      setLoading(true);

      await apiClient(`/api/council/org/units/${target.id}`, {
        method: "DELETE",
      });

      setDepartments((prev) => prev.filter((_, i) => i !== index));
      alert("삭제되었습니다.");
    } catch (error) {
      console.error("중앙집행국 삭제 오류:", error);
      alert(error.message || "중앙집행국 삭제 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const addMember = () => {
    setForm((prev) => ({
      ...prev,
      members: [...prev.members, ""],
    }));
  };

  const updateMember = (index, value) => {
    setForm((prev) => ({
      ...prev,
      members: prev.members.map((member, i) => (i === index ? value : member)),
    }));
  };

  const deleteMember = (index) => {
    setForm((prev) => ({
      ...prev,
      members:
        prev.members.length > 1
          ? prev.members.filter((_, i) => i !== index)
          : [""],
    }));
  };

  return (
    <section className="org-inner admin-scroll-x">
      <div className="section-head">
        <h1>중앙집행국</h1>

        <button
          className="pink-plus"
          onClick={openAddModal}
          type="button"
          disabled={loading}
        >
          ＋
        </button>
      </div>

      <table className="department-table">
        <thead>
          <tr>
            <th>집행국 이름</th>
            <th>국장 이름</th>
            <th>국장 전공/학번</th>
            <th>관리</th>
          </tr>
        </thead>

        <tbody>
          {loading && departments.length === 0 ? (
            <tr>
              <td colSpan="4">불러오는 중입니다.</td>
            </tr>
          ) : pagedDepartments.length > 0 ? (
            pagedDepartments.map((item, index) => {
              const originalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;

              return (
                <tr key={item.id || originalIndex}>
                  <td>{item.name}</td>
                  <td>{item.director}</td>
                  <td>{item.info}</td>

                  <td>
                    <button
                      onClick={() => openEditModal(originalIndex)}
                      type="button"
                      disabled={loading}
                    >
                      수정
                    </button>

                    <button
                      onClick={() => deleteDepartment(originalIndex)}
                      type="button"
                      disabled={loading}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="4">등록된 중앙집행국이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="org-pagination">
        <button
          type="button"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          ‹‹
        </button>

        <button
          type="button"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          ‹
        </button>

        {Array.from({ length: totalPages || 1 }, (_, index) => index + 1).map(
          (page) => (
            <button
              key={page}
              type="button"
              className={currentPage === page ? "active" : ""}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages || 1))
          }
          disabled={currentPage === (totalPages || 1)}
        >
          ›
        </button>

        <button
          type="button"
          onClick={() => setCurrentPage(totalPages || 1)}
          disabled={currentPage === (totalPages || 1)}
        >
          ››
        </button>
      </div>

      {modalOpen && (
        <div className="modal-backdrop">
          <div className="org-modal">
            <button
              className="modal-close"
              onClick={closeModal}
              type="button"
              disabled={loading}
            >
              ×
            </button>

            <h2>
              {editIndex === null ? "중앙집행국 추가" : "중앙집행국 수정"}
            </h2>

            <div className="form-row">
              <label>
                <img src={flowerIcon} alt="" className="flower-icon" />
                집행국 이름
              </label>

              <input
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="집행국 이름"
              />
            </div>

            <div className="form-row">
              <label>
                <img src={flowerIcon} alt="" className="flower-icon" />
                국장 이름
              </label>

              <input
                value={form.director}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    director: e.target.value,
                  }))
                }
                placeholder="국장 이름"
              />
            </div>

            <div className="form-row">
              <label>
                <img src={flowerIcon} alt="" className="flower-icon" />
                전공/학번
              </label>

              <input
                value={form.info}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    info: e.target.value,
                  }))
                }
                placeholder="전공/학번"
              />
            </div>

            <div className="modal-line" />

            <div className="member-title">
              <h3>국원 목록</h3>

              <button
                className="pink-plus"
                onClick={addMember}
                type="button"
                disabled={loading}
              >
                ＋
              </button>
            </div>

            {form.members.map((member, index) => (
              <div className="form-row member-row" key={index}>
                <label>
                  <img src={flowerIcon} alt="" className="flower-icon" />
                  국원 이름
                </label>

                <input
                  value={member}
                  onChange={(e) => updateMember(index, e.target.value)}
                  placeholder="국원 이름"
                />

                <button
                  onClick={() => deleteMember(index)}
                  type="button"
                  disabled={loading}
                >
                  삭제
                </button>
              </div>
            ))}

            <div className="modal-buttons">
              <button
                className="cancel-btn"
                onClick={closeModal}
                type="button"
                disabled={loading}
              >
                취소
              </button>

              <button
                className="submit-btn"
                onClick={saveDepartment}
                type="button"
                disabled={loading}
              >
                {loading ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}